import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { Empty, Flex, Spin, Table } from 'antd';
import { generateDataSource } from './documents-table.utils';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { Document } from '@/services/documents-service/documents-service.types';
import { Pagination } from '@/services/request/request.types';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import styles from './documents-table.module.scss';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { DetailsLink } from '../details-link/details-link';

interface DocumentsTableProps {
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  documentsData?: Document[];
  pagination?: Pagination;
  isAdmin?: boolean;
  currentPage: number;
  loading?: boolean;
}

export const DocumentsTable = ({
  onChange,
  documentsData,
  pagination,
  isAdmin,
  currentPage,
  loading,
}: DocumentsTableProps) => {
  const dataSource = generateDataSource(documentsData || []);

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<Document> | SorterResult<Document>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    const sortBy = sort?.field?.toString();
    const orderBy = sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined;

    onChange(pagination.current, pagination.pageSize, sortBy, orderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Document',
      dataIndex: 'name',
      key: 'name',
      className: styles.nameContainer,
      render: (name: Document['name']) => (!name ? <p>---</p> : <div>{name}</div>),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      className: styles.bodyContainer,
      render: (content: Document['content']) =>
        !content ? (
          <p>---</p>
        ) : (
          <div>{content.length > 220 ? `${content.slice(0, 220)}...` : content}</div>
        ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      className: styles.idContainer,
      render: (id: Document['id']) => <div>{id}</div>,
    },
    {
      title: 'Phone number',
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      render: (phone: Document['phone'], record) => {
        return (
          <>
            {phone ? (
              <Link to={`/documents/${record.id}`} className={styles.phoneNumber}>
                {phone}
              </Link>
            ) : (
              'n/a'
            )}
          </>
        );
      },
    },

    ...(isAdmin
      ? [
          {
            title: 'Company',
            dataIndex: 'company_name',
            key: 'company',
            className: styles.companyContainer,
            render: (company_name: Document['company_name']) => <div>{company_name || ''}</div>,
          },
        ]
      : []),

    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      className: styles.dateAndTimeContainer,
      render: (created_at: Document['created_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(created_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(created_at)}</p>
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      className: styles.dateAndTimeContainer,
      render: (updated_at: Document['updated_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(updated_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(updated_at)}</p>
          </Flex>
        </Flex>
      ),
    },

    {
      className: styles.actionContainer,
      render: (record) => <DetailsLink link={`/documents/${record.id}`} />,
    },
  ];

  return (
    <Table
      pagination={{
        current: currentPage,
        position: ['bottomCenter'],
        pageSizeOptions: ['12', '25', '50'],
        showSizeChanger: true,
        defaultPageSize: pagination?.pageItems,
        total: pagination?.totalCount,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      onChange={handleChange}
      scroll={{ x: 800 }}
      className={styles.table}
      dataSource={dataSource}
      columns={columns}
      locale={{
        emptyText: loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
};
